import { toastServerError } from "helpers";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Modal, Select } from "ui";

import { useAddEmailRecipient, useGetAdminSuggestions } from "../../apis";
import {
  EVENT_TYPE_DESCRIPTIONS,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_VALUES
} from "../../constants";
import { EEmailEventType } from "../../emailNotifications.types";
import { EmailAutocomplete } from "../EmailAutocomplete";

import styles from "./AddRecipientModal.module.scss";

interface AddRecipientModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddRecipientModal = ({ isOpen, onClose }: AddRecipientModalProps) => {
  const { t } = useTranslation();

  const { suggestions } = useGetAdminSuggestions({ enabled: isOpen });

  const [email, setEmail] = useState("");
  const [adminId, setAdminId] = useState<string | null>(null);
  const [eventType, setEventType] = useState<EEmailEventType>(
    EEmailEventType.SuspiciousBehavior
  );

  const resetForm = () => {
    setEmail("");
    setAdminId(null);
    setEventType(EEmailEventType.SuspiciousBehavior);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const { mutate, isLoading } = useAddEmailRecipient({
    onSuccess: (recipient) => {
      toast.success(
        `${t("Subscribed")} ${recipient.email} → ${t(
          EVENT_TYPE_LABELS[recipient.eventType]
        )}`
      );
      handleClose();
    },
    onError: toastServerError
  });

  const isValidEmail = EMAIL_REGEX.test(email.trim());

  const handleSubmit = () => {
    if (!isValidEmail) return;
    mutate({ email: email.trim().toLowerCase(), adminId, eventType });
  };

  const eventTypeOptions = EVENT_TYPE_VALUES.map((type) => ({
    label: `${t(EVENT_TYPE_LABELS[type])} — ${t(EVENT_TYPE_DESCRIPTIONS[type])}`,
    value: type
  }));

  return (
    <Modal
      isOpen={isOpen}
      title={t("Add recipient")}
      okText={t("Subscribe")}
      isLoading={isLoading}
      isOkDisabled={!isValidEmail}
      onOk={handleSubmit}
      onClose={handleClose}
    >
      <div className={styles.form}>
        <Select
          label={t("Event type")}
          value={eventType}
          onChange={(value) => setEventType(value as EEmailEventType)}
          options={eventTypeOptions}
        />

        <EmailAutocomplete
          label={t("Email")}
          placeholder={t("Type an email or pick an admin")}
          value={email}
          suggestions={suggestions}
          onChange={(value) => {
            setEmail(value);
            setAdminId(null);
          }}
          onSelect={(suggestion) => {
            setEmail(suggestion.email);
            setAdminId(suggestion.adminId);
          }}
        />
      </div>
    </Modal>
  );
};

export default AddRecipientModal;
